import { useDispatch, useSelector } from "react-redux";
import {
  addToFavorites,
  removeFromFavorites,
} from "store/slices/favoritesSlice";

import { ProductImage } from "common";
import { formatCurrency, getCurrentPrice } from "config/config";
import { getPrimaryProductImage } from "utils/productImages";

import { stockState, unitLabel } from "./productPresentation";

import { ProductRow } from "@/design-system";

/**
 * List listing row — the adapter for the design system's `ProductRow`
 * (`Dens N Dente Tailwind.dc.html:662-673`), and the counterpart to
 * `ProductInListGrid`. The DS owns the markup; this file owns the domain mapping.
 *
 * Same field mapping as the grid card, so the two views agree:
 * `manufacturer` → brand, `itemid` → name, `mpn` → mono sku, `sale_unit` → unit.
 *
 * This replaced 337 lines that carried separate mobile and desktop layouts, a Quick
 * Look modal importing the whole 1,126-line `pages/ProductDetail`, a
 * `<DeliveryEstimate>` pill and two copies of the cart-count bubble. All of it goes
 * for the reasons in `dnd-frontend-ui/references/deprecated.md`; the row is now one
 * layout that reflows below `md` instead of two hand-maintained ones.
 *
 * Price is passed **pre-formatted** — the row's treatment is plain bold text with the
 * unit beneath it, not `PriceTag`. `formatCurrency` per CLAUDE.md rule 1.
 */

export default function ProductInListRow({
  product,
  quantity,
  handleQuantityChange,
  handleAddToCart,
  handleNavigate,
}) {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user.info);
  const favorites = useSelector((state) => state.favorites.items);

  const id = product?.id;
  const isFavorite = favorites.includes(Number(id));

  // The old row guarded against a missing product twice; the selectors above have to
  // run unconditionally, so the bail-out comes after them.
  if (!product) return null;

  const { itemid, mpn } = product;
  const { currentPrice } = getCurrentPrice(
    product,
    quantity,
    userInfo?.category
  );

  const toggleFavorite = () => {
    dispatch(
      isFavorite ? removeFromFavorites(Number(id)) : addToFavorites(Number(id))
    );
  };

  return (
    <ProductRow
      media={
        <ProductImage
          src={getPrimaryProductImage(product)}
          alt={itemid || "Product image"}
          className="h-full w-full object-cover"
        />
      }
      brand={product.manufacturer}
      name={itemid}
      sku={mpn || undefined}
      price={formatCurrency(currentPrice)}
      unit={unitLabel(product)}
      stock={stockState(product)}
      qty={quantity}
      onQty={(value) => handleQuantityChange(id, value)}
      favorite={isFavorite}
      onFavorite={toggleFavorite}
      onAdd={handleAddToCart}
      onClick={handleNavigate}
    />
  );
}
